import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, useDisclosure } from "@heroui/react";
import { api } from "@/api";
import { toast } from "react-toastify";
import { FaPlus, FaPencil, FaTrash, FaArrowLeft } from "react-icons/fa6";
import { Office, Workplace } from "@/api/offices";
import useErrorHandling from "@/hooks/useErrorHandling";
import { CreateWorkplaceModal } from "@/app/components/modals/workplace/createWorkplaceModal";
import { EditWorkplaceModal } from "@/app/components/modals/workplace/editWorkplaceModal";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";

export function WorkplacesPage() {
  const { id } = useParams() as { id: string };
  const [office, setOffice] = useState<Office | null>(null);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState(true);

  const handleError = useErrorHandling();

  const createWorkplaceDisclosure = useDisclosure();
  const editWorkplaceDisclosure = useDisclosure();
  const fullscreenDisclosure = useDisclosure();
  const [selectedWorkplace, setSelectedWorkplace] = useState<Workplace | null>(
    null,
  );

  const loadOffice = useCallback(async () => {
    try {
      const response = await api.offices.get(Number(id));
      setOffice(response.data);
    } catch (err: any) {
      console.log(err);
      const { errorMessage } = handleError(err);
      toast.error(errorMessage);
    }
  }, [id, handleError]);

  const loadWorkplaces = useCallback(async () => {
    try {
      const response = await api.offices.workplaces.listByOffice(Number(id));
      setWorkplaces(response.data);
    } catch (err: any) {
      console.log(err);
      const { errorMessage } = handleError(err);
      toast.error(errorMessage);
    }
  }, [id, handleError]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadOffice(), loadWorkplaces()]);
      setLoading(false);
    };
    loadData();
  }, [
    loadOffice,
    loadWorkplaces,
    createWorkplaceDisclosure.isOpen,
    editWorkplaceDisclosure.isOpen,
  ]);

  const handleEditWorkplace = (workplace: Workplace) => {
    setSelectedWorkplace(workplace);
    editWorkplaceDisclosure.onOpen();
  };

  const handleDeleteWorkplace = async (workplace: Workplace) => {
    try {
      await api.offices.workplaces.delete(Number(id), workplace.id);
      toast.success("Рабочее место успешно удалено!");
      await loadWorkplaces();
    } catch (err: any) {
      console.log(err);
      const { errorMessage } = handleError(err);
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-4 items-center">
          <Button
            as={Link}
            to={`/dashboard/location/${office?.location}/office/${id}`}
            startContent={<FaArrowLeft />}
            color="default"
            variant="bordered"
            className="max-w-fit">
            Назад к офису
          </Button>
          <h1 className="text-2xl font-bold">{office?.display_name}</h1>
        </div>
        <Button
          startContent={<FaPlus />}
          color="primary"
          variant="solid"
          className="max-w-fit"
          onClick={createWorkplaceDisclosure.onOpen}>
          Создать рабочее место
        </Button>
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
        <div className="flex flex-col gap-2 mb-2">
          <div id="workplaces-table">
            <TableBuilder
              fullscreenDisclosure={fullscreenDisclosure}
              displayOptions={false}
              removeWrapper
              columns={[
                {
                  key: "display_name",
                  label: "Название",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaPencil />,
                      onClick(_value, row) {
                        handleEditWorkplace(row);
                      },
                    },
                    {
                      icon: <FaTrash />,
                      onClick(_value, row) {
                        handleDeleteWorkplace(row);
                      },
                    },
                  ],
                },
              ]}
              data={workplaces || []}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "display_name",
                direction: "ascending",
              }}
            />
          </div>
        </div>
      </div>

      <CreateWorkplaceModal
        disclosure={createWorkplaceDisclosure}
        officeId={Number(id)}
      />

      <EditWorkplaceModal
        disclosure={editWorkplaceDisclosure}
        workplace={selectedWorkplace}
        officeId={Number(id)}
      />
    </div>
  );
}
